import { Tag } from "antd";
import { FC, useState } from "react";
import {
  PROJECT_PHASE_LABELS,
  ProjectPhase,
} from "../../projectPhase";
import TaskPriorityTag, {
  TaskPriority,
} from "../TaskPriorityTag";

export interface ProjectContentTask {
  id?: string;
  project_id: string;
  phase: ProjectPhase;
  status?: string;
  title?: string;
  subtask_summary?: {
    items?: Array<{
      id?: string;
      title: string;
      reviewers: string[];
      content_items: Array<{
        item_id: string;
        assignees: string[];
        content: string;
        completed: boolean;
        start_time: number;
        due_time: number;
        priority: TaskPriority;
      }>;
    }>;
  };
}

export interface ProjectContentItemFocus {
  contentItemId?: string;
  parentTaskId?: string;
  phase: ProjectPhase;
  projectId: string;
  subtaskId?: string;
  taskId?: string;
}

interface ProjectContentItemListProps {
  getMemberDisplayName: (username: string) => string;
  includeAll: boolean;
  projectId: string;
  projectName: string;
  projectTemplate?: "phased" | "single";
  preferredAssigneeId?: string;
  tasks: ProjectContentTask[];
  userId: string;
  onOpenContentItem?: (focus: ProjectContentItemFocus) => void;
}

function formatContentItemDate(timestamp?: number) {
  return timestamp
    ? new Intl.DateTimeFormat("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date(timestamp))
    : "未设置";
}

function getContentItemText(content: string) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = content || "";
  return (
    (wrapper.textContent || "").replace(/\s+/g, " ").trim() ||
    (wrapper.querySelector("img") ? "图片内容" : "未填写内容")
  );
}

export function collectProjectContentItems(
  tasks: ProjectContentTask[],
  projectId: string,
  userId: string,
  includeAll: boolean
) {
  return tasks
    .filter(
      (task) => task.project_id === projectId && task.status !== "discarded"
    )
    .flatMap((task) =>
      (task.subtask_summary?.items || [])
        .flatMap((subtask) =>
          (subtask.content_items || [])
            .filter(
              (contentItem) =>
                includeAll || contentItem.assignees.includes(userId)
            )
            .map((contentItem) => ({
              ...contentItem,
              parentTaskId: task.id,
              parentTaskTitle: task.title || "未命名任务模块",
              phase: task.phase,
              subtaskId: subtask.id,
              subtaskTitle: subtask.title,
              reviewers: subtask.reviewers,
            }))
        )
    )
    .sort(
      (left, right) =>
        Number(left.completed) - Number(right.completed) ||
        (left.start_time || left.due_time || 0) -
          (right.start_time || right.due_time || 0)
    );
}

function getSortableDueTime(dueTime?: number) {
  return Number.isFinite(dueTime) && Number(dueTime) > 0
    ? Number(dueTime)
    : Number.POSITIVE_INFINITY;
}

export function collectVisibleProjectContentItems(
  tasks: ProjectContentTask[],
  projectId: string,
  userId: string,
  includeAll: boolean
) {
  return collectProjectContentItems(tasks, projectId, userId, includeAll)
    .filter((contentItem) => !contentItem.completed)
    .sort(
      (left, right) =>
        getSortableDueTime(left.due_time) -
          getSortableDueTime(right.due_time) ||
        (left.start_time || 0) - (right.start_time || 0) ||
        left.item_id.localeCompare(right.item_id)
    );
}

export function groupProjectContentItemsByAssignee(
  contentItems: ReturnType<typeof collectProjectContentItems>
) {
  const assigneeIds = Array.from(
    new Set(
      contentItems.flatMap((contentItem) => contentItem.assignees).filter(Boolean)
    )
  );
  return assigneeIds.map((assigneeId) => ({
    assigneeId,
    contentItems: contentItems.filter((contentItem) =>
      contentItem.assignees.includes(assigneeId)
    ),
  }));
}

export function getProjectOverdueContentCount(
  tasks: ProjectContentTask[],
  projectId: string,
  now = Date.now()
) {
  return collectProjectOverdueContentItems(tasks, projectId, now).length;
}

export function collectProjectOverdueContentItems(
  tasks: ProjectContentTask[],
  projectId: string,
  now = Date.now()
) {
  return collectProjectContentItems(tasks, projectId, "", true).filter(
    (contentItem) =>
      !contentItem.completed &&
      Number.isFinite(contentItem.due_time) &&
      contentItem.due_time > 0 &&
      contentItem.due_time < now
  );
}

const ProjectContentItemList: FC<ProjectContentItemListProps> = ({
  getMemberDisplayName,
  includeAll,
  projectId,
  projectName,
  projectTemplate = "phased",
  preferredAssigneeId,
  tasks,
  userId,
  onOpenContentItem,
}) => {
  const contentItems = collectVisibleProjectContentItems(
    tasks,
    projectId,
    userId,
    includeAll
  );
  const heading = includeAll ? "全部未完成内容" : "我的未完成内容";
  const memberGroups = includeAll
    ? groupProjectContentItemsByAssignee(contentItems)
    : [];
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const preferredAssigneeExists = memberGroups.some(
    (group) => group.assigneeId === preferredAssigneeId
  );
  const selectedAssigneeExists = memberGroups.some(
    (group) => group.assigneeId === selectedAssigneeId
  );
  const activeAssigneeId = preferredAssigneeExists
    ? preferredAssigneeId || ""
    : selectedAssigneeExists
    ? selectedAssigneeId
    : memberGroups[0]?.assigneeId || "";
  const visibleContentItems = includeAll
    ? memberGroups.find((group) => group.assigneeId === activeAssigneeId)
        ?.contentItems || []
    : contentItems;

  return (
    <div className="projectMemberContentItems">
      <div className="projectMemberContentItems__header">
        <strong>{heading}</strong>
        <span>{contentItems.length} 项</span>
      </div>
      {includeAll && memberGroups.length ? (
        <div
          aria-label={`${projectName} 负责人筛选`}
          className="projectMemberContentItems__members"
          role="tablist"
        >
          {memberGroups.map((group) => {
            const displayName =
              getMemberDisplayName(group.assigneeId) || group.assigneeId;
            const selected = group.assigneeId === activeAssigneeId;
            return (
              <button
                aria-selected={selected}
                className={selected ? "is-active" : ""}
                key={group.assigneeId}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedAssigneeId(group.assigneeId);
                }}
                role="tab"
                title={`${displayName}：${group.contentItems.length} 项任务内容`}
                type="button"
              >
                <span>{displayName.slice(0, 1).toUpperCase()}</span>
                <strong>{displayName}</strong>
                <small>{group.contentItems.length}</small>
              </button>
            );
          })}
        </div>
      ) : null}
      {contentItems.length ? (
        <div
          aria-label={`${projectName} ${heading}`}
          className="projectMemberContentItems__scroller"
          onWheel={(event) => {
            if (
              event.currentTarget.scrollWidth <=
              event.currentTarget.clientWidth
            ) {
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            event.currentTarget.scrollLeft += event.deltaY || event.deltaX;
          }}
        >
          {visibleContentItems.map((contentItem) => {
            const overdue =
              !contentItem.completed && contentItem.due_time < Date.now();
            const stateClassName = contentItem.completed
              ? "is-completed"
              : overdue
              ? "is-overdue"
              : "is-pending";
            const contentText = getContentItemText(contentItem.content);
            const assigneeNames = contentItem.assignees
              .map(getMemberDisplayName)
              .filter(Boolean);
            const reviewerNames = contentItem.reviewers
              .map(getMemberDisplayName)
              .filter(Boolean);
            const participantText = [
              `负责人：${assigneeNames.join("、") || "未分配"}`,
              reviewerNames.length
                ? `审核人：${reviewerNames.join("、")}`
                : "无需审核",
            ].join(" · ");
            const openContentItem = () => {
              if (!onOpenContentItem) return;
              onOpenContentItem({
                contentItemId: contentItem.item_id,
                parentTaskId: contentItem.parentTaskId,
                phase: contentItem.phase,
                projectId,
                subtaskId: contentItem.subtaskId,
                taskId: contentItem.parentTaskId,
              });
            };

            return (
              <article
                aria-label={
                  onOpenContentItem ? `查看内容项 ${contentText}` : undefined
                }
                className={`projectMemberContentCard ${stateClassName}`}
                key={`${contentItem.parentTaskId || contentItem.phase}-${
                  contentItem.subtaskId || contentItem.subtaskTitle
                }-${contentItem.item_id}`}
                onClick={(event) => {
                  if (!onOpenContentItem) return;
                  event.stopPropagation();
                  openContentItem();
                }}
                onKeyDown={(event) => {
                  if (
                    !onOpenContentItem ||
                    (event.key !== "Enter" && event.key !== " ")
                  ) {
                    return;
                  }
                  event.preventDefault();
                  event.stopPropagation();
                  openContentItem();
                }}
                role={onOpenContentItem ? "button" : undefined}
                tabIndex={onOpenContentItem ? 0 : undefined}
              >
                <div className="projectMemberContentCard__badges">
                  <Tag className={stateClassName}>
                    {contentItem.completed
                      ? "已完成"
                      : overdue
                      ? "已逾期"
                      : "未完成"}
                  </Tag>
                  <TaskPriorityTag priority={contentItem.priority} />
                </div>
                <strong title={contentText}>{contentText}</strong>
                <div className="projectMemberContentCard__context">
                  <small>所属子任务</small>
                  <span title={contentItem.subtaskTitle}>
                    {projectTemplate === "single"
                      ? `任务视图 · ${contentItem.subtaskTitle}`
                      : `${PROJECT_PHASE_LABELS[contentItem.phase]} · ${
                          contentItem.subtaskTitle
                        }`}
                  </span>
                </div>
                <div
                  className="projectMemberContentCard__people"
                  title={participantText}
                >
                  {participantText}
                </div>
                <div className="projectMemberContentCard__schedule">
                  <span>
                    <small>预期开始</small>
                    <b>{formatContentItemDate(contentItem.start_time)}</b>
                  </span>
                  <i />
                  <span>
                    <small>预期完结</small>
                    <b>{formatContentItemDate(contentItem.due_time)}</b>
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="projectMemberTaskEmpty">
          {includeAll ? "暂无未完成任务内容" : "暂无我参与的未完成任务内容"}
        </div>
      )}
    </div>
  );
};

export default ProjectContentItemList;
